import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { AxiosInstance } from 'axios';
import { useApiMutation } from './useApiMutation';

// App-wide billing banner (issue 213). ONE bar, ONE state: the tenant's payments
// enforcement ladder is warning → blocked, and the read-only `degraded` stage is
// gone. That makes this bar load-bearing — it is the only warning a member gets
// before the workspace locks out entirely — so it is shown to EVERY member and it
// names the day the lockout lands (Tenant.enforcementDeadline).
//
// The bar is cause-aware and action-first (issue 213 F13b/F14/F35). The ACTION
// leads (the title is the concrete ask — "Update your card", "Add a payment
// method"); the reason and the lockout date are the demoted supporting
// message. What the ask IS depends on the open case's cause + sub-cause, read from
// `/plan/standing`:
//  - a soft decline (funds, temporary) is NEVER told to replace a working card — it
//    says we'll retry and to ensure the payment method has funds;
//  - a hard decline (or an unknown payment cause) is "your card was declined —
//    update it";
//  - a chargeback (a bank-initiated reversal, NOT a card rejection — Mollie also
//    revokes the mandate) says the bank reversed a payment so it is outstanding
//    again, and asks the member to add a payment method to settle it; it is never
//    told "your card was declined" (issue 213 MED2, mirroring the chargeback mail);
//  - an authentication-required case asks the member to confirm the payment with
//    their bank (SCA);
//  - a trial that expired asks for a payment method.
// The copy mirrors the enforcement-notifier / nudge phrasing per cause so the mail
// and the banner say the same thing.
//
// The ending differs by role: only the billing-manager set (owner ∨ billing_manager
// ∨ suite_admin) gets a CTA to the workspace billing page and the cause-aware copy
// (it is the only set that may call /plan/standing, whose role the endpoint itself
// re-checks). Everyone else is pointed at their administrator, not at a link they'd
// 403 on, with a cause-agnostic line (a non-manager never learns the cause).
//
// Both the stage+deadline+role flags (`/tenant/billing-status`, user-api — the
// single source for every SPA) and the cause (`fetchStanding`, the /plan/standing
// read that only the billing-manager set may call) are injected: each app wires
// its own clients, workspace URL, load guard and per-request config. Both fetches
// are non-critical — a failure leaves the banner hidden (status) or falls back to
// the generic action wording (standing) rather than blocking the chrome. The
// billing.banner.* strings are app-kit's own shared vocabulary (ADR 0026 §7): one
// bar owned by one composable says the same thing everywhere, so the copy lives here
// and is merged into each app rather than pasted into seven catalogs × nine locales
// that would then drift apart.

export interface BillingBannerProps {
  title: string;
  message: string;
  ctaLabel?: string;
  ctaHref?: string;
  // An ACTION CTA (SCA recovery): with a label and NO href the bar renders a button
  // that runs this instead of a link. With an href it is the link's click hook (an
  // app with a router keeps a same-document click out of a full reload).
  ctaAction?: (event?: MouseEvent) => void;
  ctaBusy?: boolean; // the action is running: the button is disabled (no double POST)
  errorText?: string; // a short inline line under the message, the action stays usable
}

// The subset of `/plan/standing` the banner reads (issue 213 F13b). `cause` is the
// coarse ladder cause and `blocksUpgrade` still keys on it; `subCause` refines a
// payment cause so a soft decline is never told to replace a working card. The
// refinement field is optional: an app's own fallback standing (before the catalog
// loads) legitimately omits it.
export interface BillingStanding {
  cause: string | null; // payment_failed | trial_expired | null
  blocksUpgrade: boolean;
  subCause?: string | null; // soft_decline | hard_decline | authentication_required | null
}

interface BillingStatus {
  stage: string | null;
  deadline: string | null;
  isManager: boolean;
}

export function useBillingBanner(opts: {
  api: AxiosInstance; // user-api client (billing-status is membership-gated, no admin needed)
  workspaceUrl: string | (() => string);
  // Reads /plan/standing (system-admin-api). Only ever called for a manager, whose
  // role the endpoint itself re-checks; the returned cause/subCause drive the
  // cause-aware title, body and CTA.
  fetchStanding: () => Promise<BillingStanding | null>;
  canLoad?: () => boolean; // return false to skip (and clear) the fetch
  requestConfig?: () => Record<string, unknown>; // e.g. { headers: { 'X-Tenant-Slug': slug } }
  // The active tenant's LIFECYCLE status, for an app that can put a member in front
  // of a workspace they cannot log into — i.e. one with a tenant switcher
  // (workspace-ui). A member of both an active and a suspended tenant signs in via
  // the active one and may switch to the suspended one; `GET /api/user/tenants` does
  // not filter it out of the switcher. Apps without a switcher omit this: their
  // member never reaches a suspended tenant, because the login refusal already
  // stopped them.
  tenantStatus?: () => string | null;
  // First-paint guard (issue 213 F25). `tenantStatus` reads a store the app loads
  // lazily, so on first paint it is null and the locked bar is ABSENT until a later
  // navigation refills the store — exactly when a locked-out member most needs the
  // one explanation they get. An app that supplies `tenantStatus` also supplies this
  // (its store's load): the composable awaits it at the START of every load, so the
  // status source is resolved by the time the bar mounts and the locked bar shows on
  // first paint, not a navigation later. Best-effort — the store owns its own errors.
  ensureTenantStatus?: () => Promise<unknown>;
  // payment-api client (same-origin `/api/payment`, tenant-scoped like the rest of the
  // app's clients). With it, the authentication_required CTA is a real payment: it
  // POSTs `/billing/recovery-payment-session` and sends the browser to the hosted
  // checkout. Without it the CTA stays a link to the billing page.
  paymentApi?: AxiosInstance;
  // An app with a router (workspace-ui) turns a plain click on the CTA link into an
  // in-app navigation to this path (+ query) instead of a full page load.
  navigateInApp?: (path: string) => void;
}) {
  const { t, locale } = useI18n();
  const billingStatus = ref<BillingStatus | null>(null);
  const standing = ref<BillingStanding | null>(null);
  const recoveryError = ref(false);

  async function loadBillingStatus(): Promise<void> {
    // F25: resolve the lifecycle-status source BEFORE anything reads it — the
    // suspended arm below keys on it and it must be present on first paint. This
    // runs before the canLoad gate so a first-paint tenant whose billing slug is not
    // resolved yet still gets its status loaded.
    if (opts.ensureTenantStatus) {
      await opts.ensureTenantStatus().catch(() => undefined);
    }
    if (opts.canLoad && !opts.canLoad()) {
      billingStatus.value = null;
      standing.value = null;
      return;
    }
    try {
      const cfg = { ...(opts.requestConfig?.() ?? {}) };
      const { data } = await opts.api.get('/tenant/billing-status', cfg as never);
      const d = data as {
        stage?: string | null;
        deadline?: string | null;
        isOwner?: boolean;
        isBillingManager?: boolean;
        isSuiteAdmin?: boolean;
      } | null;
      billingStatus.value = {
        stage: d?.stage ?? null,
        deadline: d?.deadline ?? null,
        // The billing-manager set, matching the /plan endpoints' own gate.
        isManager: !!(d?.isOwner || d?.isBillingManager || d?.isSuiteAdmin),
      };
    } catch {
      billingStatus.value = null;
      standing.value = null;
      return; // non-critical — the banner just stays hidden
    }
    // Only a manager sees a CTA and the cause-aware copy, so only a manager needs
    // the cause; a non-manager never calls /plan/standing (it would 403).
    standing.value = null;
    if (billingStatus.value.stage !== 'warning' || !billingStatus.value.isManager) return;
    try {
      standing.value = await opts.fetchStanding();
    } catch {
      /* non-critical — fall back to the generic action wording */
    }
  }

  // A plain left click on a link inside an app that owns the router stays in the
  // document; a modified click (new tab) and an app without a router get the href.
  function followInApp(e: MouseEvent | undefined, path: string): void {
    if (!opts.navigateInApp || !e) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    opts.navigateInApp(path);
  }

  // The SCA recovery click (ADR 0023 §7): POST for a hosted checkout and go there.
  // The amount is never sent — payment-api prices the open debt itself. Through the
  // mutation seam like every other write: `pending` disables the button for the
  // request, and on success the page is leaving, so `recoveryLeaving` keeps it
  // disabled rather than inviting a second click.
  let recoveryFallback: { path: string; href: string } | null = null;
  const recoveryLeaving = ref(false);
  const recovery = useApiMutation<string | null, []>({
    tier: 'inlineEdit',
    errorChannel: 'inPlace', // the inline `recoveryError` line under the bar's message
    run: async () => {
      const cfg = { ...(opts.requestConfig?.() ?? {}), suppressGlobalError: true };
      const { data } = await opts.paymentApi!.post('/billing/recovery-payment-session', {}, cfg as never);
      return (data as { checkoutUrl?: string } | null)?.checkoutUrl ?? null;
    },
    onSuccess: (url) => {
      if (!url) {
        recoveryError.value = true;
        return;
      }
      recoveryLeaving.value = true;
      window.location.href = url;
    },
    onError: (outcome) => {
      const settledElsewhere =
        outcome.code === 'NO_RECOVERY_DUE' ||
        outcome.code === 'RECOVERY_IN_FLIGHT' ||
        outcome.code === 'RECOVERY_PAYMENT_PROCESSING';
      if (outcome.status === 409 && settledElsewhere && recoveryFallback) {
        // The case is already gone (paid elsewhere, or closed), or a payment is
        // already with the bank: the billing page's card section shows the real
        // state, which a generic "could not be started" line would not.
        if (opts.navigateInApp) opts.navigateInApp(recoveryFallback.path);
        else window.location.href = recoveryFallback.href;
        return;
      }
      recoveryError.value = true;
    },
  });
  const recoveryBusy = computed(() => recovery.pending.value || recoveryLeaving.value);

  async function startRecoveryPayment(path: string, href: string): Promise<void> {
    if (!opts.paymentApi || recoveryBusy.value) return;
    recoveryError.value = false;
    recoveryFallback = { path, href };
    await recovery.mutate();
  }

  // The workspace-ui billing surface's date convention (issue 213), reused so the
  // one shared bar reads the same in every app.
  //
  // Rendered in UTC, matching the stage mail the worker sends (enforcement-notifier's
  // own formatDeadline). The deadline is a UTC billing INSTANT, not a wall-clock
  // moment the viewer experiences: enforcementDeadline=2026-08-01T01:00:00Z is the
  // 1 August billing date, and a member in UTC−5 rendering it host-locally would read
  // "Jul 31, 2026" off this bar while the mail in their inbox says "1 August 2026" —
  // two authoritative dates for one lockout, and the mail is the one they can forward
  // and quote back at us. The browser must not reinterpret it.
  function formatDeadline(iso: string): string {
    return new Date(iso).toLocaleDateString(locale.value, { dateStyle: 'medium', timeZone: 'UTC' });
  }

  // The demoted lockout consequence (F14): secondary supporting text that always
  // names the day, appended after the cause-specific action/reason.
  function lockoutClause(deadline: string): string {
    return t('billing.banner.lockoutOn', { date: formatDeadline(deadline) });
  }

  // The cause-aware, action-first content for a manager at 'warning' (F13b/F35):
  // the ACTION is the title, the reason + the lockout date the demoted message, and
  // the CTA label the cause-appropriate ask. Driven off standing.cause + subCause.
  //
  // `tab` is the deep-link tab hint the CTA href carries (issue 213 C4 H2): a cause
  // whose ask is to add/fix a card — any payment_failed sub-cause, AND an expired
  // trial (whose CTA is literally "Add a payment method") — sends the manager
  // straight to the Zahlungsmethode tab (`?tab=paymentMethod`) where the card form
  // lives, so the CTA lands on the action instead of the default Pläne tab. The
  // review (generic) case omits it and keeps the plain /billing (Plans) target.
  function managerContent(deadline: string): { titleKey: string; message: string; ctaKey: string; tab?: string; focus?: boolean; pay?: boolean } {
    const s = standing.value;
    const cause = s?.cause ?? null;
    const lock = lockoutClause(deadline);

    if (cause === 'trial_expired') {
      // The trial ask IS to add a payment method (the card/mandate-setup form on
      // the Zahlungsmethode tab), so its CTA deep-links there like the card-fix
      // causes — not to Plans (issue 213 C4 H2).
      return {
        titleKey: 'billing.banner.trialTitle',
        message: `${t('billing.banner.trialBody')} ${lock}`,
        ctaKey: 'billing.banner.trialCta',
        tab: 'paymentMethod',
        focus: true,
      };
    }
    if (cause === 'payment_failed') {
      const sub = s?.subCause ?? null;
      if (sub === 'soft_decline') {
        return {
          titleKey: 'billing.banner.softDeclineTitle',
          message: `${t('billing.banner.softDeclineBody')} ${lock}`,
          ctaKey: 'billing.banner.softDeclineCta',
          tab: 'paymentMethod',
          focus: true,
        };
      }
      if (sub === 'authentication_required') {
        return {
          titleKey: 'billing.banner.scaTitle',
          message: `${t('billing.banner.scaBody')} ${lock}`,
          ctaKey: 'billing.banner.scaCta',
          tab: 'paymentMethod',
          focus: true,
          pay: true,
        };
      }
      if (sub === 'chargeback') {
        // A chargeback is a bank-initiated reversal, not a card rejection, so the
        // hard-decline "your card was declined" copy would be factually wrong
        // (issue 213 MED2). Mollie revoked the mandate, so the honest ask is to add
        // a payment method and settle the reopened debt — the payment-method tab.
        return {
          titleKey: 'billing.banner.chargebackTitle',
          message: `${t('billing.banner.chargebackBody')} ${lock}`,
          ctaKey: 'billing.banner.chargebackCta',
          tab: 'paymentMethod',
          focus: true,
        };
      }
      // hard_decline OR an unknown/absent sub-cause: the card was declined, update it.
      return {
        titleKey: 'billing.banner.fixCardTitle',
        message: `${t('billing.banner.fixCardBody')} ${lock}`,
        ctaKey: 'billing.banner.fixCardCta',
        tab: 'paymentMethod',
        focus: true,
      };
    }
    // Cause unknown (e.g. the standing read failed): a generic, still action-first
    // ask — never a threat-first line, never a wrong remedy.
    return {
      titleKey: 'billing.banner.reviewTitle',
      message: `${t('billing.banner.reviewBody')} ${lock}`,
      ctaKey: 'billing.banner.reviewCta',
    };
  }

  const billingBanner = computed<BillingBannerProps | null>(() => {
    // The LOCKOUT ITSELF comes first — it is not a warning about a future date, it
    // is the state the warning was about, and it must win over any stale stage.
    //
    // This bar cannot be driven off `stage` here: entering 'blocked' suspends the
    // tenant AND nulls enforcementStage, so /tenant/billing-status reports
    // stage:null and every stage-driven branch below goes quiet. Without this arm a
    // locked-out workspace renders NOTHING — bare 404s on every tile and on
    // /billing, with no explanation. The tenant's own status is the only thing still
    // saying what happened. (F25 keeps it present on first paint, not after a nav.)
    //
    // 'blocked' rides along with 'suspended' because user-api's login refusal treats
    // the two as one state (tenantLoginDecision → workspace_suspended), and this bar
    // must not disagree with the door.
    //
    // No CTA for anyone, manager or not: /billing 404s on a suspended tenant like
    // every other tile, and nobody inside the tenant can lift this — only support
    // can. So the copy points where the login refusal points ("contact us") rather
    // than at an administrator who is just as locked out, and G11's rule holds a
    // fortiori: no link anyone would 403 on.
    const lifecycle = opts.tenantStatus?.();
    if (lifecycle === 'suspended' || lifecycle === 'blocked') {
      return { title: t('billing.banner.suspendedTitle'), message: t('billing.banner.suspendedMessage') };
    }
    const status = billingStatus.value;
    // No open case, or a case with no date to name: nothing to warn about.
    if (status?.stage !== 'warning' || !status.deadline) return null;
    if (!status.isManager) {
      // No CTA (the billing page 403s for them): lead with the one thing they CAN do
      // — reach their administrator — and demote the lockout consequence. Cause-
      // agnostic, because /plan/standing is manager-only.
      return {
        title: t('billing.banner.contactTitle'),
        message: `${t('billing.banner.contactBody')} ${lockoutClause(status.deadline)}`,
      };
    }
    const { titleKey, message, ctaKey, tab, focus, pay } = managerContent(status.deadline);
    const wsUrl = typeof opts.workspaceUrl === 'function' ? opts.workspaceUrl() : opts.workspaceUrl;
    // A card-fix cause deep-links to the payment-method tab so the CTA lands on the
    // card form, not the default Pläne tab (issue 213 C4 H2); `focus=card` makes
    // BillingView scroll to and highlight the card section, then drop the param.
    // Other causes keep the plain /billing target (Plans). BillingView reads these
    // on mount and on every later change of the query.
    const path = tab ? `/billing?tab=${tab}${focus ? '&focus=card' : ''}` : '/billing';
    const href = `${(wsUrl || '').replace(/\/$/, '')}${path}`;
    if (pay && opts.paymentApi) {
      // authentication_required: the CTA is the payment itself (ADR 0023 §7), not a
      // link — the member pays the open debt on-session at the hosted checkout.
      return {
        title: t(titleKey),
        message,
        ctaLabel: t(ctaKey),
        ctaAction: () => void startRecoveryPayment(path, href),
        ctaBusy: recoveryBusy.value,
        ...(recoveryError.value ? { errorText: t('billing.banner.recoveryError') } : {}),
      };
    }
    return {
      title: t(titleKey),
      message,
      ctaLabel: t(ctaKey),
      ctaHref: href,
      ...(opts.navigateInApp ? { ctaAction: (e?: MouseEvent) => followInApp(e, path) } : {}),
    };
  });

  return { billingStatus, loadBillingStatus, billingBanner };
}
